/**
 * Quantity Validation Utility
 *
 * Centralized validation for quantity fields across Prepaid and Postpaid forms.
 * Uses configuration from tds.config.json for validation rules.
 */

import config from '../tds.config.json';

/**
 * Get quantity validation configuration for a specific form and item type
 * @param {string} formType - 'prepaid' or 'postpaid'
 * @param {string} itemType - Item type (e.g., 'MSISDN', 'VOBB', 'Blank Sim')
 * @returns {object} Configuration with min, max, and errorMessages
 */
export const getQuantityConfig = (formType, itemType) => {
  const itemConfig = config.quantityValidation[formType]?.[itemType] || {};
  const defaultConfig = config.quantityValidation.default;

  return {
    min: itemConfig.min || defaultConfig.min,
    max: itemConfig.max || defaultConfig.max,
    errorMessages: {
      ...defaultConfig.errorMessages,
      ...itemConfig.errorMessages
    }
  };
};

/**
 * Validate a quantity value
 * @param {string|number} value - The quantity value to validate
 * @param {string} formType - 'prepaid' or 'postpaid'
 * @param {string} itemType - Item type (e.g., 'MSISDN', 'VOBB', 'Blank Sim')
 * @returns {string} Error message if invalid, empty string if valid
 */
export const validateQuantity = (value, formType, itemType) => {
  const config = getQuantityConfig(formType, itemType);

  // Check if value is empty
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return config.errorMessages.required;
  }

  // Check for leading zeros (e.g., "01", "02", "001")
  const stringValue = String(value).trim();
  if (stringValue.length > 1 && stringValue.startsWith('0')) {
    return "Quantity can't start with 0";
  }

  const num = Number(value);

  // Check if value is a valid positive integer
  if (Number.isNaN(num) || !Number.isInteger(num) || num <= 0) {
    return config.errorMessages.notInteger;
  }

  // Check minimum constraint
  if (num < config.min) {
    return (config.errorMessages.belowMin || config.errorMessages.outOfRange)
      .replace('{min}', config.min)
      .replace('{max}', config.max);
  }

  // Check maximum constraint
  if (num > config.max) {
    return (config.errorMessages.aboveMax || config.errorMessages.outOfRange)
      .replace('{min}', config.min)
      .replace('{max}', config.max);
  }

  return ''; // No error
};

/**
 * Get minimum quantity for a specific item type
 * @param {string} formType - 'prepaid' or 'postpaid'
 * @param {string} itemType - Item type (e.g., 'MSISDN', 'VOBB', 'Blank Sim')
 * @returns {number} Minimum quantity value
 */
export const getQuantityMin = (formType, itemType) => {
  return getQuantityConfig(formType, itemType).min;
};

/**
 * Get maximum quantity for a specific item type
 * @param {string} formType - 'prepaid' or 'postpaid'
 * @param {string} itemType - Item type (e.g., 'MSISDN', 'VOBB', 'Blank Sim')
 * @returns {number} Maximum quantity value
 */
export const getQuantityMax = (formType, itemType) => {
  return getQuantityConfig(formType, itemType).max;
};

/**
 * Prevents non-integer input in quantity fields
 * Use this as an onKeyDown handler to block decimal points and other non-integer characters
 * @param {KeyboardEvent} e - The keyboard event
 * @returns {boolean} false if key should be prevented, true otherwise
 */
export const preventNonIntegerInput = (e) => {
  // List of keys to block for integer-only input
  const blockedKeys = [
    '.',
    ',',
    'e',      
    'E',
    '+',      
    '-'      
  ];

  // Check if the pressed key should be blocked
  if (blockedKeys.includes(e.key)) {
    e.preventDefault();
    return false;
  }

  // Block all alphabetic characters (a-z, A-Z)
  // Check if key is a single character and is alphabetic
  if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
    e.preventDefault();
    return false;
  }

  return true;
};
